import { useState, type KeyboardEvent } from 'react';
import styled from 'styled-components';
import { Input } from 'antd';
import { ArrowRightIcon } from '@phosphor-icons/react';
import { colors, motion, radii, spacing, typography } from '../../theme-v2';

const { TextArea } = Input;

export interface ChatInputBarProps {
  /** Controlled input value. When undefined, the component manages its own state. */
  value?: string;
  /** Fired on every keystroke / paste. */
  onChange?: (value: string) => void;
  /**
   * Fired when the user submits (Enter without shift, or click the send
   * button). Whitespace-only values do not trigger this. The value is
   * passed back trimmed.
   */
  onSubmit?: (value: string) => void;
  /** Fired when the textarea gains focus. */
  onFocus?: () => void;
  /** Translated send-button aria-label. Consumer-supplied (no default). */
  sendAriaLabel: string;
  /** Translated placeholder. Consumer-supplied. */
  placeholder?: string;
  disabled?: boolean;
  /** Disable submission (e.g. while a response is streaming). */
  submitting?: boolean;
  /** Auto-grow row range. Defaults to {minRows: 1, maxRows: 4}. */
  autoSize?: { minRows: number; maxRows: number };
  className?: string;
}

/**
 * ChatInputBar — bottom-of-thread input row for the Chatbot panel.
 * Auto-growing textarea + send button. Enter submits, Shift+Enter
 * inserts a newline (standard chat ergonomic).
 *
 * Controlled (`value` + `onChange`) or uncontrolled. `onSubmit` fires
 * with the trimmed value; the component clears its own state on submit
 * only when uncontrolled.
 *
 * The send button is a self-contained styled.button consuming theme-v2
 * tokens directly so it renders correctly regardless of any surrounding
 * AntD ConfigProvider context.
 */
export const ChatInputBar = ({
  value: valueProp,
  onChange,
  onSubmit,
  onFocus,
  sendAriaLabel,
  placeholder,
  disabled,
  submitting,
  autoSize = { minRows: 1, maxRows: 4 },
  className,
}: ChatInputBarProps) => {
  const [internalValue, setInternalValue] = useState('');
  const isControlled = valueProp !== undefined;
  const value = isControlled ? valueProp : internalValue;

  const handleChange = (next: string) => {
    if (!isControlled) setInternalValue(next);
    onChange?.(next);
  };

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled || submitting) return;
    onSubmit?.(trimmed);
    if (!isControlled) setInternalValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const canSubmit = value.trim().length > 0 && !disabled && !submitting;

  return (
    <StyledWrapper className={className}>
      <StyledInputBox>
        <StyledTextArea
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          placeholder={placeholder}
          disabled={disabled}
          autoSize={autoSize}
          variant="borderless"
        />
      </StyledInputBox>
      <StyledSendButton
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        aria-label={sendAriaLabel}
      >
        <ArrowRightIcon size={18} weight="bold" />
      </StyledSendButton>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${spacing.sm}px;
`;

const StyledInputBox = styled.div`
  flex: 1 1 0%;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: 36px;
  padding: 6px ${spacing.md}px;
  background: ${colors.surface.muted};
  border: 1px solid ${colors.border.subtle};
  border-radius: ${radii.md}px;
  /* Explicit theme font — form controls don't pick up the page font on
     routes without a global font style (media-player); the textarea below
     inherits from this box. */
  font-family: ${typography.fontFamily};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: border-color ${motion.duration.fast} ${motion.easing.standard};

  &:focus-within {
    border-color: ${colors.accent.base};
  }
`;

const StyledTextArea = styled(TextArea)`
  flex: 1;
  font-family: inherit;
  font-size: inherit;
  color: inherit;
  line-height: ${typography.lineHeight.body};
  resize: none;
  padding: 0;
  background: transparent;

  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const StyledSendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: ${radii.md}px;
  background: ${colors.accent.base};
  color: ${colors.text.onAccent};
  cursor: pointer;
  flex-shrink: 0;
  transition: background ${motion.duration.fast} ${motion.easing.standard};

  &:hover:not(:disabled) {
    background: ${colors.accent.hover};
  }

  &:active:not(:disabled) {
    background: ${colors.accent.active};
  }

  &:focus-visible {
    outline: 2px solid ${colors.accent.base};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;
